import { useCallback, useEffect, useRef, useState } from 'react';

export interface PageRange {
    min: number;
    max: number;
}

export interface ScrollPositionData {
    page: number;
    scrollToItemId: string;
}

export interface UseBidirectionalScrollConfig {
    /** Cache key for storing scroll position in localStorage (e.g., 'VEHICLESFILTER') */
    cacheKey: string;
    /** Items per page */
    itemsPerPage: number;
    /** Container ID for scroll adjustments (default: 'data-table-container') */
    containerId?: string;
}

export interface InfiniteScrollProps {
    isLoading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    hasMorePrevious: boolean;
    isLoadingPrevious: boolean;
    onLoadPrevious: (() => Promise<void>) | undefined;
}

export interface UseBidirectionalScrollReturn {
    // State
    loadedPageRange: PageRange;
    isLoadingPrevious: boolean;
    isScrollRestoring: boolean;
    scrollToRowId: string | null;

    // Refs (for use in async functions)
    shouldLoadAdjacentPages: React.MutableRefObject<boolean>;
    pendingScrollToId: React.MutableRefObject<string | null>;
    scrollHeightBeforePrepend: React.MutableRefObject<number>;

    // State setters
    setLoadedPageRange: React.Dispatch<React.SetStateAction<PageRange>>;
    setIsLoadingPrevious: React.Dispatch<React.SetStateAction<boolean>>;
    setIsScrollRestoring: React.Dispatch<React.SetStateAction<boolean>>;
    setScrollToRowId: React.Dispatch<React.SetStateAction<string | null>>;

    /** Reset all scroll state (call when filters/sort change) */
    resetScrollState: () => void;

    /** Calculate actual page from item index in the list */
    calculatePageFromIndex: (itemIndex: number) => number;

    /** Save scroll position to cache (call before navigating to details) */
    saveScrollPosition: (itemId: string, itemIndex: number, currentFilters: Record<string, unknown>) => void;

    /**
     * Check cache for scroll position to restore
     * Returns data for any page (including page 1)
     */
    getScrollRestoreData: () => ScrollPositionData | null;

    /**
     * Initiate scroll restoration from cached data
     * Handles both page 1 (immediate highlight) and page > 1 (bidirectional scroll)
     * @returns the page number to start loading from
     */
    initiateRestoration: (scrollData: ScrollPositionData) => number;

    /** Trigger scroll to a specific item (sets scrollToRowId directly) */
    triggerScrollToItem: (itemId: string) => void;

    /** Called after data loads when shouldLoadAdjacentPages is true - sets the scroll target */
    finishRestoration: () => void;

    /** Clear scroll position from cache */
    clearScrollPositionFromCache: (currentFilters: Record<string, unknown>) => void;

    /** Prepare for loading previous page - stores scroll height */
    prepareLoadPrevious: () => void;

    /** Call after prepending items to adjust scroll position (use in useEffect with items dependency) */
    adjustScrollAfterPrepend: () => void;

    /** Check if has more items to load */
    getHasMore: (itemsLength: number, totalItems: number) => boolean;

    /** Check if has more previous items to load */
    getHasMorePrevious: () => boolean;

    /** Get onLoadMore function (returns no-op if restoring) */
    getOnLoadMore: (loadMore: () => void) => () => void;

    /** Get onLoadPrevious function (returns undefined if restoring) */
    getOnLoadPrevious: (loadPrevious: () => Promise<void>) => (() => Promise<void>) | undefined;

    /**
     * Get the complete infinite scroll props object for DataTable
     * This is the recommended way to pass props to DataTable/DataTableWithSidebar
     */
    getInfiniteScrollProps: (
        isLoading: boolean,
        totalItems: number,
        itemsLength: number,
        onLoadMore: () => void,
        onLoadPrevious: () => Promise<void>
    ) => InfiniteScrollProps;
}

function useBidirectionalScroll({
    cacheKey,
    itemsPerPage,
    containerId = 'data-table-container',
}: UseBidirectionalScrollConfig): UseBidirectionalScrollReturn {
    // State
    const [loadedPageRange, setLoadedPageRange] = useState<PageRange>({ min: 1, max: 1 });
    const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
    const [isScrollRestoring, setIsScrollRestoring] = useState(false);
    const [scrollToRowId, setScrollToRowId] = useState<string | null>(null);

    // Refs
    const scrollHeightBeforePrepend = useRef<number>(0);
    const pendingScrollToId = useRef<string | null>(null);
    const shouldLoadAdjacentPages = useRef(false);

    // Clear scrollToRowId after scrolling and re-enable infinite scroll
    useEffect(() => {
        if (scrollToRowId) {
            // Re-enable infinite scroll after scroll animation completes
            // 500ms gives time for: scroll animation (300-400ms) + browser rendering + observer settling
            const scrollRestoringTimeout = setTimeout(() => {
                setIsScrollRestoring(false);
            }, 500);
            // Keep the highlight visible for the full animation duration (3 seconds)
            const highlightTimeout = setTimeout(() => {
                setScrollToRowId(null);
                // Clear scrollToItemId from cache so it doesn't re-highlight on navigation return
                try {
                    const cached = localStorage.getItem(cacheKey);
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        if (parsed.scrollToItemId) {
                            delete parsed.scrollToItemId;
                            localStorage.setItem(cacheKey, JSON.stringify(parsed));
                        }
                    }
                } catch { /* ignore parse errors */ }
            }, 3000);
            return () => {
                clearTimeout(scrollRestoringTimeout);
                clearTimeout(highlightTimeout);
            };
        }
    }, [scrollToRowId]);

    // Reset all scroll state
    const resetScrollState = useCallback(() => {
        setLoadedPageRange({ min: 1, max: 1 });
        setIsScrollRestoring(false);
        setScrollToRowId(null);
        shouldLoadAdjacentPages.current = false;
        pendingScrollToId.current = null;
    }, []);

    // Calculate actual page from item index
    const calculatePageFromIndex = useCallback((itemIndex: number): number => {
        const pageOffset = Math.floor(itemIndex / itemsPerPage);
        return loadedPageRange.min + pageOffset;
    }, [itemsPerPage, loadedPageRange.min]);

    // Save scroll position to cache
    const saveScrollPosition = useCallback((
        itemId: string,
        itemIndex: number,
        currentFilters: Record<string, unknown>
    ) => {
        const actualPage = calculatePageFromIndex(itemIndex);
        const filters = { ...currentFilters, page: actualPage, scrollToItemId: itemId };
        localStorage.setItem(cacheKey, JSON.stringify(filters));
    }, [cacheKey, calculatePageFromIndex]);

    // Get scroll restore data from cache (works for any page including page 1)
    const getScrollRestoreData = useCallback((): ScrollPositionData | null => {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return null;

        try {
            const parsed = JSON.parse(cached);
            if (parsed.scrollToItemId) {
                return {
                    page: parsed.page || 1,
                    scrollToItemId: parsed.scrollToItemId,
                };
            }
        } catch {
            // Invalid cache data
        }
        return null;
    }, [cacheKey]);

    // Initiate scroll restoration - handles both page 1 and page > 1
    const initiateRestoration = useCallback((scrollData: ScrollPositionData): number => {
        const startPage = scrollData.page;

        // Always set isScrollRestoring to prevent infinite scroll during restoration
        setIsScrollRestoring(true);

        if (startPage > 1) {
            // For pages > 1, use bidirectional scroll restoration
            shouldLoadAdjacentPages.current = true;
            pendingScrollToId.current = scrollData.scrollToItemId;
            setLoadedPageRange({ min: startPage, max: startPage });
        } else {
            // For page 1, just highlight the row (no need for bidirectional scroll)
            setScrollToRowId(scrollData.scrollToItemId);
            setLoadedPageRange({ min: 1, max: 1 });
        }

        return startPage;
    }, []);

    // Trigger scroll to a specific item directly
    const triggerScrollToItem = useCallback((itemId: string) => {
        setScrollToRowId(itemId);
    }, []);

    // Finish restoration - called after adjacent pages are loaded
    const finishRestoration = useCallback(() => {
        if (pendingScrollToId.current) {
            setScrollToRowId(pendingScrollToId.current);
            pendingScrollToId.current = null;
        }
        shouldLoadAdjacentPages.current = false;
    }, []);

    // Clear scroll position from cache
    const clearScrollPositionFromCache = useCallback((currentFilters: Record<string, unknown>) => {
        const filters = { ...currentFilters };
        delete (filters as Record<string, unknown>).scrollToItemId;
        (filters as Record<string, unknown>).page = 1;
        localStorage.setItem(cacheKey, JSON.stringify(filters));
    }, [cacheKey]);

    // Prepare for loading previous page - stores current scroll height
    const prepareLoadPrevious = useCallback(() => {
        const container = document.getElementById(containerId);
        scrollHeightBeforePrepend.current = container?.scrollHeight || 0;
    }, [containerId]);

    // Adjust scroll position after prepending items
    const adjustScrollAfterPrepend = useCallback(() => {
        if (scrollHeightBeforePrepend.current > 0) {
            const container = document.getElementById(containerId);
            if (container) {
                const scrollHeightAfter = container.scrollHeight;
                const heightAdded = scrollHeightAfter - scrollHeightBeforePrepend.current;
                if (heightAdded > 0) {
                    container.scrollTop += heightAdded;
                }
            }
            scrollHeightBeforePrepend.current = 0;
        }
    }, [containerId]);

    // Computed helpers for props
    const getHasMore = useCallback((itemsLength: number, totalItems: number): boolean => {
        // Calculate total pages and check if we're already at the last page
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const atLastPage = loadedPageRange.max >= totalPages;
        return !isScrollRestoring && itemsLength < totalItems && !atLastPage;
    }, [isScrollRestoring, itemsPerPage, loadedPageRange.max]);

    const getHasMorePrevious = useCallback((): boolean => {
        return !isScrollRestoring && loadedPageRange.min > 1;
    }, [isScrollRestoring, loadedPageRange.min]);

    const getOnLoadMore = useCallback((loadMore: () => void): () => void => {
        return isScrollRestoring ? () => { /* noop during scroll restoration */ } : loadMore;
    }, [isScrollRestoring]);

    const getOnLoadPrevious = useCallback((
        loadPrevious: () => Promise<void>
    ): (() => Promise<void>) | undefined => {
        return isScrollRestoring ? undefined : loadPrevious;
    }, [isScrollRestoring]);

    // Get complete infinite scroll props for DataTable
    const getInfiniteScrollProps = useCallback((
        isLoading: boolean,
        totalItems: number,
        itemsLength: number,
        onLoadMore: () => void,
        onLoadPrevious: () => Promise<void>
    ): InfiniteScrollProps => {
        return {
            isLoading,
            hasMore: getHasMore(itemsLength, totalItems),
            onLoadMore: getOnLoadMore(onLoadMore),
            hasMorePrevious: getHasMorePrevious(),
            isLoadingPrevious,
            onLoadPrevious: getOnLoadPrevious(onLoadPrevious),
        };
    }, [getHasMore, getHasMorePrevious, getOnLoadMore, getOnLoadPrevious, isLoadingPrevious]);

    return {
        // State
        loadedPageRange,
        isLoadingPrevious,
        isScrollRestoring,
        scrollToRowId,

        // Refs
        shouldLoadAdjacentPages,
        pendingScrollToId,
        scrollHeightBeforePrepend,

        // State setters
        setLoadedPageRange,
        setIsLoadingPrevious,
        setIsScrollRestoring,
        setScrollToRowId,

        // Actions
        resetScrollState,
        calculatePageFromIndex,
        saveScrollPosition,
        getScrollRestoreData,
        initiateRestoration,
        triggerScrollToItem,
        finishRestoration,
        clearScrollPositionFromCache,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,

        // Props helpers
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
        getInfiniteScrollProps,
    };
}

export default useBidirectionalScroll;
